import React, { useEffect, useState } from 'react';
import { getChatsByDate, getChatDetail } from '../../services/admin/adminApi';
import ChatListByDate from '../../components/admin/ChatListByDate';
import ChatDetail from '../../components/admin/ChatDetail';
import TimeRangeSelector from '../../components/admin/TimeRangeSelector';
import Pagination from '../../components/admin/Pagination';
import GoogleAnalyticsButton from '../../components/admin/GoogleAnalyticsButton';

// Types for chat data
interface ChatListResponse {
  dates: Array<{
    date: string;
    display_date: string;
    chats: Array<{
      chat_id: string;
      timestamp: string;
      summary: string;
      persona_name: string;
      message_count: number;
    }>;
  }>;
  total_chats: number;
  page: number;
  page_size: number;
  total_pages: number;
  time_range: string;
}

const Chats: React.FC = () => {
  const [timeRange, setTimeRange] = useState<string>('7d');
  const [chatsData, setChatsData] = useState<ChatListResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [chatDetail, setChatDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  
  const [page, setPage] = useState<number>(1);
  const pageSize = 20;

  // Fetch chats data with pagination and time range
  const fetchChatsData = async () => {
    setLoading(true);
    try {
      const data = await getChatsByDate(timeRange, page, pageSize);
      setChatsData(data);
    } catch (err) {
      setError('Failed to load chats data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load chat detail when a chat is selected
  const loadChatDetail = async (chatId: string) => {
    setDetailLoading(true);
    setSelectedChatId(chatId);
    
    try {
      const detail = await getChatDetail(chatId);
      setChatDetail(detail);
    } catch (err) {
      console.error('Failed to load chat detail:', err);
      setError('Failed to load chat detail');
    } finally {
      setDetailLoading(false);
    }
  };

  // Handle time range change
  const handleTimeRangeChange = (range: string) => {
    setTimeRange(range);
    setPage(1); // Reset to first page when changing time range
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Clear chat selection to go back to list view
  const handleBack = () => {
    setSelectedChatId(null);
    setChatDetail(null);
  };

  // Load data on component mount and when time range or page changes
  useEffect(() => {
    fetchChatsData();
  }, [timeRange, page]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Chat Analytics</h2>
        <div className="flex space-x-3">
          <TimeRangeSelector 
            value={timeRange}
            onChange={handleTimeRangeChange}
          />
          <GoogleAnalyticsButton />
        </div>
      </div>
      
      {/* Show either chat list or chat detail */}
      {selectedChatId ? (
        <>
          {detailLoading ? (
            <div className="p-6 text-center">Loading chat detail...</div>
          ) : error ? (
            <div className="p-4 text-red-500 bg-red-100 rounded-md">
              <h3 className="text-lg font-semibold">Error</h3>
              <p>{error}</p>
            </div>
          ) : (
            <ChatDetail chatData={chatDetail} onBack={handleBack} />
          )}
        </>
      ) : (
        <>
          <div className="bg-white p-6 rounded-lg shadow">
            {loading ? (
              <div className="p-6 text-center">Loading chats data...</div>
            ) : error ? (
              <div className="p-4 text-red-500 bg-red-100 rounded-md">
                <h3 className="text-lg font-semibold">Error</h3>
                <p>{error}</p>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-gray-600">
                    Showing {chatsData?.dates?.reduce((sum, date) => sum + date.chats.length, 0) || 0} of {chatsData?.total_chats || 0} chats
                  </p>
                </div>
                
                <ChatListByDate 
                  dates={chatsData?.dates || []} 
                  onChatSelect={loadChatDetail} 
                />
                
                {/* Pagination controls */}
                {chatsData && chatsData.total_pages > 1 && (
                  <div className="mt-6">
                    <Pagination 
                      currentPage={page}
                      totalPages={chatsData.total_pages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Chats;
